import { useState, forwardRef, useEffect, useCallback, useRef } from "react";
import { lockBodyScroll, unlockBodyScroll } from "@/lib/scrollLock";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dumbbell,
  CreditCard,
  Home,
  LogOut,
  LayoutDashboard,
  Menu,
  X,
  Crown,
  User,
  Settings,
  ChevronDown,
  Bell,
} from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { getPackageLabel } from "@/lib/planFeatures";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import NotificationBell from "@/components/NotificationBell";
import UnreadBadge from "@/components/UnreadBadge";
import { useUnreadNotifications } from "@/hooks/useUnreadNotifications";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { subscriptionBadgeClass, type SubBadgeTone } from "@/lib/subscriptionBadgeStyles";

const PageHeader = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileUserMenuOpen, setMobileUserMenuOpen] = useState(false);
  // Separate state for the lg/xl secondary row so its hidden dropdown does not
  // open on top of the mobile user menu.
  const [tabletUserMenuOpen, setTabletUserMenuOpen] = useState(false);
  const { total: unreadNotifications } = useUnreadNotifications();
  const notificationsPath = "/notifications";

  const navigate = useNavigate();
  const location = useLocation();

  // Close all menus on route change so deep links / back navigation start fresh.
  useEffect(() => {
    setIsOpen(false);
    setUserMenuOpen(false);
    setMobileUserMenuOpen(false);
    setTabletUserMenuOpen(false);
  }, [location.pathname, location.search]);

  // Refs to the mobile triggers so focus can return to them when a menu closes.
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const mobileUserTriggerRef = useRef<HTMLButtonElement>(null);
  // Navigation / sign-out actions move focus themselves; skip focus restore then.
  const skipFocusRestore = useRef(false);

  // Lock background scroll while any mobile overlay (hamburger menu or the
  // mobile user dropdown) is open. Reference-counted so overlapping locks can
  // never leave `overflow: hidden` stuck on <body>.
  const scrollLocked = isOpen || mobileUserMenuOpen;
  useEffect(() => {
    if (!scrollLocked) return;
    lockBodyScroll();
    return () => unlockBodyScroll();
  }, [scrollLocked]);

  const { user, signOut, planName, isPremium } = useAuth();

  // Keep mobile user dropdown and hamburger menu mutually exclusive so they
  // don't render on top of each other.
  useEffect(() => {
    if (mobileUserMenuOpen) setIsOpen(false);
  }, [mobileUserMenuOpen]);

  useEffect(() => {
    if (isOpen) {
      setMobileUserMenuOpen(false);
      setTabletUserMenuOpen(false);
      setUserMenuOpen(false);
    }
  }, [isOpen]);

  // Return focus to the triggering button when a mobile overlay closes, unless
  // the close was caused by an action that moves focus itself (navigation).
  const restoreFocus = (el: HTMLElement | null) => {
    if (!el) return;
    if (skipFocusRestore.current) {
      skipFocusRestore.current = false;
      return;
    }
    // Radix settles its own focus handling (and blurs the dismissed content)
    // across a couple of frames, so evaluate where focus actually landed
    // *after* that instead of on the same tick the overlay closed.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!document.contains(el)) return;
        const active = document.activeElement as HTMLElement | null;
        // Something meaningful already holds focus (e.g. the element the user
        // tapped outside) — don't yank it away.
        if (active && active !== document.body && document.contains(active)) return;
        el.focus({ preventScroll: true });
      });
    });
  };



  const prevMobileUserMenuOpen = useRef(false);
  useEffect(() => {
    if (prevMobileUserMenuOpen.current && !mobileUserMenuOpen) {
      restoreFocus(mobileUserTriggerRef.current);
    }
    prevMobileUserMenuOpen.current = mobileUserMenuOpen;
  }, [mobileUserMenuOpen]);

  const prevIsOpen = useRef(false);
  useEffect(() => {
    if (prevIsOpen.current && !isOpen) {
      restoreFocus(hamburgerRef.current);
    }
    prevIsOpen.current = isOpen;
  }, [isOpen]);

  const { realIsAdmin, isImpersonating } = useImpersonation();
  // Real admins (not currently impersonating a member) go to /admin instead
  // of the member dashboard, since they don't have personal panel data.
  const dashboardPath = realIsAdmin && !isImpersonating ? "/admin" : "/dashboard";
  const { t, i18n } = useTranslation();
  const { data: subStatus } = useSubscriptionStatus();

  // Route the "Subscription Packages" menu entry to a context-aware section
  // of /premium based on the user's current badge status.
  const subscriptionPath = (() => {
    const s = subStatus?.status;
    if (s === "active") return "/premium#current-plan";
    if (s === "expiring" || s === "expired") return "/premium#renewal";
    return "/premium#packages";
  })();

  // Localized formatter for the subscription end date shown inside the badge tooltip.
  const formatEndsAt = (iso: string | null) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(i18n.language === "tr" ? "tr-TR" : "en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const renderSubBadge = () => {
    if (!subStatus || subStatus.status === "none") return null;
    const labelMap: Record<string, string> = {
      active: t("nav.subscriptionBadge.active"),
      expiring: t("nav.subscriptionBadge.expiring", { days: subStatus.daysRemaining ?? 0 }),
      expired: t("nav.subscriptionBadge.expired"),
    };
    const text = labelMap[subStatus.status];
    if (!text) return null;
    const endsAtLabel = formatEndsAt(subStatus.endsAt);
    return (
      <Tooltip delayDuration={150}>
        <TooltipTrigger asChild>
          <span className={`ml-auto cursor-help ${subscriptionBadgeClass(subStatus.status as SubBadgeTone)}`}>
            {text}
          </span>
        </TooltipTrigger>
        <TooltipContent side="left" className="text-xs">
          <div className="flex flex-col gap-0.5">
            {endsAtLabel ? (
              <span>
                <span className="text-muted-foreground">{t("nav.subscriptionBadge.tooltipEndsAt")}:</span>{" "}
                <span className="font-medium text-foreground">{endsAtLabel}</span>
              </span>
            ) : (
              <span className="text-muted-foreground">{t("nav.subscriptionBadge.tooltipNoEndDate")}</span>
            )}
            {subStatus.status === "expired" ? (
              <span className="text-destructive">{t("nav.subscriptionBadge.tooltipExpired")}</span>
            ) : subStatus.daysRemaining !== null ? (
              <span className="text-foreground">
                {t("nav.subscriptionBadge.tooltipDaysLeft", { days: subStatus.daysRemaining })}
              </span>
            ) : null}
          </div>
        </TooltipContent>
      </Tooltip>
    );
  };

  // Active plan tier — only render the badge when the user actually has a
  // recognized active subscription (avoids "Starter" label for guests/free
  // accounts whose `planName` is null). getPackageLabel handles both raw tier
  // slugs ("PREMIUM") and already-localized names ("Strength in Range").
  const tierLabel = isPremium && planName ? getPackageLabel(planName) || null : null;

  // Videolar ve Beslenme header menüsünden tamamen kaldırıldı (üyeler dahil);
  // bu içeriklere panel ve program sayfaları üzerinden erişilir.
  const navItems = [
    { path: "/programs", label: t("nav.programs"), icon: Dumbbell },
    // Online Randevu navigasyondan geçici olarak gizlendi; ileride yeniden aktif edilecek.
    // { path: "/appointments", label: t("nav.appointments"), icon: Calendar },
    { path: "/premium", label: t("nav.membership"), icon: CreditCard },
  ];

  const closeAll = () => {
    setIsOpen(false);
    setUserMenuOpen(false);
    setMobileUserMenuOpen(false);
  };

  const handleNav = (path: string) => {
    // Navigation renders a new page; don't yank focus back to the header trigger.
    skipFocusRestore.current = true;
    closeAll();
    navigate(path);
  };

  const handleUserAction = (fn: () => void, isMobile = false) => {
    skipFocusRestore.current = true;
    if (isMobile) setMobileUserMenuOpen(false);
    else setUserMenuOpen(false);
    setTimeout(fn, 0);
  };

  // Shared user dropdown content used by desktop, secondary-row and mobile triggers.
  const UserMenuContent = ({ isMobile = false }: { isMobile?: boolean }) => (
    <>
      <DropdownMenuLabel className="flex flex-col gap-0.5">
        <span className="text-xs uppercase tracking-wider text-muted-foreground font-normal">{t("nav.myAccount")}</span>
        <span className="text-sm font-display text-foreground truncate">
          {(user?.user_metadata as any)?.full_name || user?.email}
        </span>
        {(user?.user_metadata as any)?.full_name && (
          <span className="text-[11px] text-muted-foreground truncate">{user?.email}</span>
        )}
        {isMobile && tierLabel && (
          <div className="mt-1">
            <TierBadge compact interactive={false} />
          </div>
        )}
      </DropdownMenuLabel>
      <DropdownMenuSeparator />
      <DropdownMenuItem
        onSelect={() =>
          handleUserAction(
            () => navigate(unreadNotifications > 0 ? `${notificationsPath}?unread=1` : notificationsPath),
            isMobile,
          )
        }
        className="cursor-pointer"
      >
        <Bell size={14} className="mr-2 text-muted-foreground" />
        <span>{t("notifications.title", "Bildirimler")}</span>
        <UnreadBadge count={unreadNotifications} className="ml-auto" />
      </DropdownMenuItem>
      <DropdownMenuItem
        onSelect={() => handleUserAction(() => navigate(`${dashboardPath}?tab=profile`), isMobile)}
        className="cursor-pointer"
      >
        <User size={14} className="mr-2 text-muted-foreground" />
        {t("nav.profile")}
      </DropdownMenuItem>
      <DropdownMenuItem
        onSelect={() => handleUserAction(() => navigate(subscriptionPath), isMobile)}
        className="cursor-pointer"
      >
        <CreditCard size={14} className="mr-2 text-muted-foreground" />
        <span>{t("nav.subscriptionPackages")}</span>
        {renderSubBadge()}
      </DropdownMenuItem>
      <DropdownMenuItem
        onSelect={() => handleUserAction(() => navigate(`${dashboardPath}?tab=measurements`), isMobile)}
        className="cursor-pointer"
      >
        <Settings size={14} className="mr-2 text-muted-foreground" />
        {t("nav.settings")}
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem
        onSelect={() => handleUserAction(signOut, isMobile)}
        className="cursor-pointer text-destructive focus:text-destructive"
      >
        <LogOut size={14} className="mr-2" />
        {t("nav.logout")}
      </DropdownMenuItem>
    </>
  );

  // Compact tier badge shown in header rows.
  const TierBadge = ({ compact = false, interactive = true }: { compact?: boolean; interactive?: boolean }) => {
    if (!tierLabel) return null;
    const className = `inline-flex items-center gap-1 font-semibold tracking-wider uppercase text-primary bg-primary/10 border border-primary/30 rounded transition-colors ${
      compact ? "h-8 px-1.5 text-[10px]" : "h-9 gap-1 px-2 text-[10px]"
    } ${interactive ? "hover:bg-primary/15" : ""}`;
    const children = (
      <>
        <Crown size={compact ? 11 : 12} />
        <span>{tierLabel}</span>
      </>
    );
    if (!interactive) {
      return (
        <span className={className} aria-label={`${tierLabel} ${t("dashboard.subscriptionStatus")}`}>
          {children}
        </span>
      );
    }
    return (
      <button
        onClick={() => navigate("/premium")}
        className={className}
        aria-label={`${tierLabel} ${t("dashboard.subscriptionStatus")}`}
      >
        {children}
      </button>
    );
  };

  // Dashboard button with optional label to save horizontal space on narrower desktops.
  const DashboardBtn = ({ showLabel = true }: { showLabel?: boolean }) => {
    const isActive = location.pathname === dashboardPath;
    return (
      <Tooltip delayDuration={150}>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={`h-9 gap-1.5 px-2 lg:px-3 text-[11px] lg:text-xs font-medium whitespace-nowrap shrink-0 transition-all ${
              isActive
                ? "text-primary bg-primary/15 border border-primary/30 shadow-sm shadow-primary/10"
                : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
            }`}
            onClick={() => navigate(dashboardPath)}
            aria-label={realIsAdmin && !isImpersonating ? "Admin Dashboard" : t("nav.dashboard")}
          >
            <LayoutDashboard size={14} />
            {showLabel && <span>{realIsAdmin && !isImpersonating ? "Admin Dashboard" : t("nav.dashboard")}</span>}
          </Button>
        </TooltipTrigger>
        {!showLabel && (
          <TooltipContent side="bottom" className="text-xs">
            {realIsAdmin && !isImpersonating ? "Admin Dashboard" : t("nav.dashboard")}
          </TooltipContent>
        )}
      </Tooltip>
    );
  };

  // Home button with optional label.
  const HomeBtn = ({ showLabel = true }: { showLabel?: boolean }) => {
    const isActive = location.pathname === "/";
    return (
      <Tooltip delayDuration={150}>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={`h-9 gap-1.5 px-2 lg:px-3 text-[11px] lg:text-xs font-medium whitespace-nowrap shrink-0 transition-all ${
              isActive
                ? "text-primary bg-primary/15 border border-primary/30 shadow-sm shadow-primary/10"
                : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
            }`}
            onClick={() => navigate("/")}
            aria-label={t("nav.home")}
          >
            <Home size={14} />
            {showLabel && <span>{t("nav.home")}</span>}
          </Button>
        </TooltipTrigger>
        {!showLabel && (
          <TooltipContent side="bottom" className="text-xs">
            {t("nav.home")}
          </TooltipContent>
        )}
      </Tooltip>
    );
  };

  // User dropdown trigger used in desktop and secondary rows.
  const UserDropdownTrigger = forwardRef<HTMLButtonElement, { compact?: boolean }>(({ compact = false }, ref) => (
    <Button
      ref={ref}
      variant="ghost"
      size="sm"
      className={`h-9 gap-1.5 px-2 lg:px-3 text-[11px] lg:text-xs text-muted-foreground hover:text-foreground ${compact ? "xl:hidden" : ""}`}
      aria-label={t("nav.userMenu")}
    >
      <div className="h-6 w-6 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center text-primary">
        <User size={12} />
      </div>
      {!compact && (
        <span className="hidden xl:inline max-w-[120px] truncate">
          {(user?.user_metadata as any)?.full_name || user?.email?.split("@")[0]}
        </span>
      )}
      <ChevronDown size={12} className="opacity-60" />
    </Button>
  ));
  UserDropdownTrigger.displayName = "UserDropdownTrigger";

  // Render the main navigation items (Programs, Membership).
  const MainNav = () => (
    <nav
      className="hidden lg:flex items-center justify-center flex-1 min-w-0 gap-0.5 lg:gap-1 overflow-hidden"
      aria-label="Page navigation"
    >
      {navItems.map((item) => {
        const isActive = location.pathname === item.path;
        return (
          <Button
            key={item.path}
            variant="ghost"
            size="sm"
            className={`h-9 gap-1.5 px-2 lg:px-3 text-[11px] lg:text-xs font-medium whitespace-nowrap shrink-0 transition-all ${
              isActive
                ? "text-primary bg-primary/15 border border-primary/30 shadow-sm shadow-primary/10"
                : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
            }`}
            onClick={() => navigate(item.path)}
          >
            <item.icon size={14} />
            <span>{item.label}</span>
          </Button>
        );
      })}
      <LanguageSwitcher className="ml-1" />
    </nav>
  );

  // Full user action bar used on xl+ (single-row header) and on the secondary row at lg.
  // `open`/`onOpenChange` let the secondary row manage its own menu state so its
  // hidden trigger does not leak a portaled dropdown on top of the mobile user menu.
  const UserActions = ({
    compact = false,
    open,
    onOpenChange,
  }: {
    compact?: boolean;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
  }) => {
    const isControlled = open !== undefined && onOpenChange !== undefined;
    const menuOpen = isControlled ? open : compact ? mobileUserMenuOpen : userMenuOpen;
    const setMenuOpen = isControlled ? onOpenChange : compact ? setMobileUserMenuOpen : setUserMenuOpen;
    return (
      <div
        className={`flex items-center ${compact ? "gap-1.5" : "gap-0.5"} ${compact ? "" : "ml-1 border-l border-border/30 pl-1"}`}
      >
        {user ? (
          <>
            <TierBadge compact={compact} />
            <NotificationBell />
            <DashboardBtn showLabel={!compact} />
            <HomeBtn showLabel={!compact} />
            <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen} modal={false}>
              <DropdownMenuTrigger asChild>
                <UserDropdownTrigger compact={compact} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60 bg-popover border-border z-[60]">
                <UserMenuContent isMobile={compact} />
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        ) : (
          <Button variant="hero" size="sm" onClick={() => navigate("/auth")}>
            {t("nav.login")}
          </Button>
        )}
      </div>
    );
  };

  return (
    <TooltipProvider delayDuration={150}>
      <header className="glass-card border-b border-border/30 sticky top-0 z-50">
        <div className="container mx-auto">
          {/* Top row: logo, main nav (lg+), full user actions (xl+), mobile toggle (<lg) */}
          <div className="flex items-center justify-between h-14 md:h-16">
            {/* Logo */}
            <a
              href="/"
              onClick={(e) => {
                e.preventDefault();
                navigate("/");
              }}
              className="flex items-center gap-2 min-w-0 shrink"
            >
              <img src="/logo.png" alt="PT Metin Ozdemir" className="h-8 w-auto shrink-0" />
              <span className="font-display text-base sm:text-lg xl:text-2xl tracking-wider text-foreground truncate">
                PT METİN <span className="text-gradient-gold">ÖZDEMİR</span>
              </span>
            </a>

            <MainNav />

            {/* Full user actions — only on xl+ */}
            {user && (
              <div className="hidden xl:flex items-center shrink-0">
                <UserActions />
              </div>
            )}

            {/* Guest login — visible from lg up (top row) */}
            {!user && (
              <div className="hidden lg:flex items-center shrink-0 ml-2">
                <Button variant="hero" size="sm" onClick={() => navigate("/auth")}>
                  {t("nav.login")}
                </Button>
              </div>
            )}

            {/* Mobile / Tablet toggle */}
            <div className="flex lg:hidden items-center gap-1.5 shrink-0">
              <LanguageSwitcher compact />
              {!user && (
                <Button variant="hero" size="sm" className="h-8 px-3 text-xs" onClick={() => navigate("/auth")}>
                  {t("nav.login")}
                </Button>
              )}
              {user && (
                <button
                  type="button"
                  onClick={() => navigate(unreadNotifications > 0 ? "/notifications?unread=1" : "/notifications")}
                  aria-label={t("notifications.title", "Bildirimler")}
                  className="relative h-8 w-8 inline-flex items-center justify-center rounded-full border border-border/40 text-muted-foreground"
                >
                  <Bell size={15} />
                  <UnreadBadge count={unreadNotifications} className="absolute -top-1 -right-1" />
                </button>
              )}
              {user && (
                <DropdownMenu open={mobileUserMenuOpen} onOpenChange={setMobileUserMenuOpen} modal={false}>
                  <DropdownMenuTrigger asChild>
                    <button
                      ref={mobileUserTriggerRef}
                      className="h-8 inline-flex items-center gap-1.5 px-1.5 rounded-full border border-primary/30 bg-primary/10 hover:bg-primary/20 transition-colors"
                      aria-label={t("nav.userMenu")}
                      aria-expanded={mobileUserMenuOpen}
                    >
                      <div className="h-6 w-6 rounded-full bg-primary/20 flex items-center justify-center text-primary">
                        <User size={12} />
                      </div>
                      <ChevronDown size={12} className="text-primary/80 mr-0.5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    side="bottom"
                    align="end"
                    sideOffset={8}
                    alignOffset={48}
                    collisionPadding={8}
                    className="w-52 bg-popover border-border z-[60] max-h-[calc(100dvh-5.5rem)] overflow-y-auto"
                  >
                    <UserMenuContent isMobile />
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              <button
                ref={hamburgerRef}
                className="h-8 w-8 inline-flex items-center justify-center text-foreground"
                aria-label={isOpen ? t("nav.closeMenu") : t("nav.openMenu")}
                aria-expanded={isOpen}
                onClick={() => setIsOpen(!isOpen)}
              >
                {isOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          </div>

          {/* Secondary row: compact user actions at lg (1024–1279px) where a single row overflows */}
          {user && (
            <div className="hidden lg:flex xl:hidden items-center justify-end gap-2 py-2 border-t border-border/20">
              <UserActions compact open={tabletUserMenuOpen} onOpenChange={setTabletUserMenuOpen} />
            </div>
          )}
        </div>

        {/* Mobile / Tablet menu — portaled to <body> so ancestor transforms (page
          transitions) cannot shrink or clip the fixed overlay. */}
        {createPortal(
          <AnimatePresence initial={false}>
            {isOpen && (
              <>
                {/* Backdrop */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.18, ease: "linear" }}
                  onClick={() => setIsOpen(false)}
                  className="lg:hidden fixed inset-0 top-14 md:top-16 bg-background/80 backdrop-blur-sm z-40 transform-gpu will-change-[opacity]"
                  aria-hidden="true"
                  data-testid="mobile-menu-backdrop"
                />
                {/* Menu panel */}
                <motion.div
                  initial={{ opacity: 0, y: -12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{
                    type: "spring",
                    stiffness: 420,
                    damping: 34,
                    mass: 0.7,
                    opacity: { duration: 0.16, ease: "easeOut" },
                  }}
                  style={{ backfaceVisibility: "hidden" }}
                  className="lg:hidden fixed left-0 right-0 top-14 md:top-16 z-50 glass-card border-b border-border/30 shadow-2xl transform-gpu will-change-[transform,opacity]"
                  data-testid="mobile-menu-panel"
                >
                  <div className="container mx-auto py-6 max-h-[calc(100dvh-3.5rem)] md:max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch] flex flex-col gap-3">
                    {navItems.map((item) => {
                      const isActive = location.pathname === item.path;
                      return (
                        <button
                          key={item.path}
                          onClick={() => handleNav(item.path)}
                          className={`text-base font-medium transition-colors text-left flex items-center gap-3 ${
                            isActive ? "text-primary" : "text-muted-foreground hover:text-primary"
                          }`}
                        >
                          <item.icon size={18} />
                          {item.label}
                        </button>
                      );
                    })}

                    <div className="border-t border-border/30 pt-3 mt-1 flex flex-col gap-3">
                      {user ? (
                        <>
                          <div className="flex items-center gap-3 px-1 pb-2 border-b border-border/30">
                            <div className="h-9 w-9 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center text-primary">
                              <User size={16} />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-display text-foreground truncate">
                                {(user.user_metadata as any)?.full_name || user.email?.split("@")[0]}
                              </p>
                              <p className="text-[11px] text-muted-foreground truncate">{user.email}</p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleNav(dashboardPath)}
                            className={`text-base font-medium transition-colors text-left flex items-center gap-3 ${
                              location.pathname === dashboardPath
                                ? "text-primary"
                                : "text-muted-foreground hover:text-primary"
                            }`}
                          >
                            <LayoutDashboard size={18} />
                            {realIsAdmin && !isImpersonating ? "Admin Dashboard" : t("nav.dashboard")}
                          </button>
                          <button
                            onClick={() => handleNav(`${dashboardPath}?tab=profile`)}
                            className="text-base font-medium text-muted-foreground hover:text-primary transition-colors text-left flex items-center gap-3"
                          >
                            <User size={18} />
                            {t("nav.profile")}
                          </button>
                          <button
                            onClick={() =>
                              handleNav(unreadNotifications > 0 ? "/notifications?unread=1" : "/notifications")
                            }
                            className={`text-base font-medium transition-colors text-left flex items-center gap-3 ${
                              location.pathname === "/notifications"
                                ? "text-primary"
                                : "text-muted-foreground hover:text-primary"
                            }`}
                          >
                            <Bell size={18} />
                            <span>{t("notifications.title", "Bildirimler")}</span>
                            <UnreadBadge count={unreadNotifications} className="ml-1" />
                          </button>
                          <button
                            onClick={() => handleNav(subscriptionPath)}
                            className="text-base font-medium text-muted-foreground hover:text-primary transition-colors text-left flex items-center gap-3"
                          >
                            <CreditCard size={18} />
                            {t("nav.subscriptionPackages")}
                          </button>
                          <button
                            onClick={() => handleNav(`${dashboardPath}?tab=measurements`)}
                            className="text-base font-medium text-muted-foreground hover:text-primary transition-colors text-left flex items-center gap-3"
                          >
                            <Settings size={18} />
                            {t("nav.settings")}
                          </button>
                          <button
                            onClick={() => handleNav("/")}
                            className="text-base font-medium text-muted-foreground hover:text-primary transition-colors text-left flex items-center gap-3"
                          >
                            <Home size={18} />
                            {t("nav.home")}
                          </button>
                          <Button
                            variant="outline"
                            size="lg"
                            className="mt-2"
                            onClick={() => {
                              setIsOpen(false);
                              signOut();
                            }}
                          >
                            <LogOut size={16} />
                            {t("nav.logout")}
                          </Button>
                        </>
                      ) : (
                        <Button variant="hero" size="lg" className="mt-2" onClick={() => handleNav("/auth")}>
                          {t("nav.login")}
                        </Button>
                      )}
                    </div>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>,
          document.body,
        )}
      </header>
    </TooltipProvider>
  );
};

export default PageHeader;
